import type { IVideo } from "@aix/vidmax-shared/src/constants/prismaTypings"
import { EVideoTemplate } from "@aix/vidmax-shared/src/remotion/schema/project"
import { createFakeTextProjectProps } from "@aix/vidmax-shared/src/utils/projectProps"
import { isFakeTextVideo } from "@aix/vidmax-shared/src/utils/validation"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import { VideoEditorHeader } from "~/components/Templates/Common/VideoEditorHeader"
import VideoEditorPage from "~/components/Templates/Common/VideoEditorPage"
import { FakeTextEditorProvider } from "~/components/Templates/FakeText/FakeTextEditorContext"
import { useFakeTextEditor } from "~/components/Templates/FakeText/useFakeTextEditor"
import { FakeTextEditorPlayground } from "~/components/Templates/FakeText/FakeTextEditorPlayground"
import { api } from "~/utils/api"

const FakeTextEditorPage = () => {
  const { videoId } = Route.useParams()

  // Fetch video data
  const {
    data: video,
    isLoading,
    error,
  } = api.video.getVideoById.useQuery(
    {
      id: videoId,
    },
    {
      enabled: !!videoId,
      refetchOnWindowFocus: false,
    },
  )

  // Create the project props for the editor
  const projectProps = useMemo(() => {
    if (!video) return null
    return createFakeTextProjectProps(video)
  }, [video])

  if (!video || !projectProps || !isFakeTextVideo(video)) {
    return (
      <VideoEditorPage
        video={video}
        isLoading={isLoading}
        error={error}
        expectedTemplateType={EVideoTemplate.FAKE_TEXT}
      />
    )
  }

  return (
    <FakeTextEditorProvider video={video} projectProps={projectProps}>
      <FakeTextEditorContent />
    </FakeTextEditorProvider>
  )
}

// Component that uses the FakeTextEditor context
const FakeTextEditorContent = () => {
  const {
    video,
    renderStatus,
    saveChanges,
    isUpdating,
    undo,
    redo,
    history,
    saveAndRender,
    isDurationValid,
    estimatedDuration,
    checkDurationBeforeAction,
    hasChanges,
  } = useFakeTextEditor()

  // Function to get the video title
  const getTitleText = (video: IVideo) => {
    if (isFakeTextVideo(video)) {
      return video.title || `Chat Video ${video.id}`
    }
    return `Chat Video ${video.id}`
  }

  useEffect(() => {
    // Add styles to <html> tag
    const html = document.documentElement
    const previousStyle = html.getAttribute("style")

    html.style.overflow = "hidden"

    // Optional: cleanup on unmount
    return () => {
      if (previousStyle !== null) {
        html.setAttribute("style", previousStyle)
      } else {
        html.removeAttribute("style")
      }
    }
  }, [])

  return (
    <VideoEditorPage
      video={video}
      isLoading={false}
      error={null}
      expectedTemplateType={EVideoTemplate.FAKE_TEXT}
      renderStatus={renderStatus}
      getTitleText={getTitleText}
    >
      <div className="flex flex-col flex-1 overflow-hidden">
        <VideoEditorHeader
          title={video.title || "Fake Text Editor"}
          video={video}
          saveChanges={saveChanges}
          isUpdating={isUpdating}
          undo={undo}
          redo={redo}
          canUndo={history.past.length > 0}
          canRedo={history.future.length > 0}
          saveAndRender={saveAndRender}
          renderStatus={renderStatus}
          isDurationValid={isDurationValid}
          estimatedDuration={estimatedDuration}
          checkDurationBeforeAction={checkDurationBeforeAction}
          hasChanges={hasChanges}
        />
        <FakeTextEditorPlayground />
      </div>
    </VideoEditorPage>
  )
}

export const Route = createFileRoute("/videos/$videoId/editor/fake-text")({
  component: FakeTextEditorPage,
})
